import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import MomentUtils from '@date-io/moment';
import { KeyboardDatePicker, KeyboardDatePickerProps, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import 'moment/locale/it';
import { snakeCase } from 'snake-case';

import { addOrUpdate } from '../../redux/modelsInfo/modelsInfoSlice';
import { ReduxState } from '../../redux/types';
import api from '../../utils/api';

/*
 * This component wrap the Input component, provided by Material-UI.
 * It implements some basic logis common to all the inputs in the application.
 */

export type Props = Omit<KeyboardDatePickerProps, 'onChange' | 'variant' | 'onError' | 'value'> & {
    name: string;
    data: any;
    handleAttributeChange?: (name: string, value: any) => void;
    handleKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    partialTranslationKey?: string;
    errorIfEmpty?: boolean
    booleanValues?: boolean;
    flex?: number | string;
    helpText?: string;
    translationKey?: string;
};

const DatePicker = (props: Props) => {
    const { name, data, handleAttributeChange, handleKeyDown, partialTranslationKey, errorIfEmpty, booleanValues, helpText, translationKey, ...otherProps } = props;
    const { select, disabled, label } = otherProps;

    const { t } = useTranslation();

    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    const modelsInfo = useSelector((state: ReduxState) => state.modelsInfo);
    const dispatch = useDispatch();

    const inputRef = useRef<HTMLDivElement>(null);
    const [hasFocus, setHasFocus] = useState(false);

    const onChange = (newDate: MaterialUiPickersDate) => {
        const value = newDate?.format('YYYY-MM-DD') ?? '';
        return handleAttributeChange && handleAttributeChange(name, value);
    };

    const capitalizeFirstLetter = (str: string) => {
        if (!str || str.length < 1) return str;
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const realTranslationKey = translationKey ?? (partialTranslationKey + '.' + snakeCase(name));
    const realLabel = label ?? (partialTranslationKey ? capitalizeFirstLetter(t(realTranslationKey)) : name);

    // nested key names are passed as strings in the format 'a.b.c.d...', the next line manage this case and get the value of the input
    const realValue = name.split('.').reduce((value, entry) => value ? value[entry] : undefined, data);

    // check if the model attribute is required
    let isAttributeRequired = false;
    const splitTranslationKey = realTranslationKey.split('.');

    if (splitTranslationKey.length >= 4 && splitTranslationKey[0] === 'models' && splitTranslationKey[2] === 'attributes') {
        const modelName = splitTranslationKey[1];
        const attributeName = splitTranslationKey[3];

        // get the model info from redux state
        const thisModelInfo = modelsInfo.find(model => model.name === modelName);

        if (!thisModelInfo) {
            // if the redux state doesn't contain this model info, fetch them from the API, and save them in redux
            dispatch(addOrUpdate({ name: modelName, requiredAttributes: [] }));

            api.request('/models/' + splitTranslationKey[1] + '/info').then(res => {
                dispatch(addOrUpdate({
                    name: modelName,
                    requiredAttributes: res.requiredAttributes,
                    translatableAttributes: res.translatableAttributes
                }));
            });
        }
        else {
            // otherwise check if the attribute is required
            isAttributeRequired = thisModelInfo.requiredAttributes.some(attribute => attribute === attributeName);
        }
    }

    useEffect(() => {
        // When an input is disabled and re-enabled, it lose the focus.
        // This effect give the focus back to the last focused input, when re-enabled.
        if (!disabled && hasFocus && inputRef.current && !select) {
            inputRef.current.getElementsByTagName('input')[0]?.focus();
        }
    }, [disabled, hasFocus, inputRef, select]);

    return (
        <MuiPickersUtilsProvider locale='it' utils={MomentUtils}>
            <KeyboardDatePicker
                ref={inputRef}
                margin='dense'
                label={realLabel}
                inputVariant='outlined'
                format='DD/MM/YYYY'
                // @ts-ignore
                value={realValue}
                // @ts-ignore
                onChange={onChange}
                open={isDatePickerOpen}
                onClick={() => {
                    setIsDatePickerOpen(true);
                }}
                onClose={() => setIsDatePickerOpen(false)}
                onBlur={() => setHasFocus(false)}
                onFocus={() => setHasFocus(true)}
                onKeyDown={(e) => {
                    e.preventDefault();
                    return false;
                }}
                style={{ flex: 1, marginTop: '14px', marginBottom: '4px' }}
                inputProps={{ tabIndex: -1 }}
                KeyboardButtonProps={{ tabIndex: -1 }}
                fullWidth
                {...((errorIfEmpty && { error: (!realValue || realValue.length === 0) }))}
                {...(isAttributeRequired && { required: true })}
                {...otherProps}
            />
        </MuiPickersUtilsProvider>
    );
};

const compareProps = (oldProps: any, newProps: any) => {
    // return true if the old and new props should be treated as the same

    const name = newProps.name;
    const keys = Object.keys(newProps);

    if (keys.some(key => key !== 'data' && oldProps[key] !== newProps[key])) {
        // if a prop (except data) is not equal in old and new props, props are differents
        return false;
    }

    const realValueOld = name.split('.').reduce((value: any, entry: any) => value ? value[entry] : undefined, oldProps.data);
    const realValueNew = name.split('.').reduce((value: any, entry: any) => value ? value[entry] : undefined, newProps.data);

    if (realValueOld !== realValueNew) {
        // if the current input value is different, props are differents
        return false;
    }

    // Otherwise, treat props as if they're the same.
    // We don't care about the other attributes in data, because they don't interact with this component instance.
    return true;
};

// memo is used to prevent useless re-render, that could cause performance issues
export default memo(DatePicker, compareProps);
